import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ArchiveRestore, Download, Eye, Trash2, Upload } from "lucide-react";
import type { LibraryFile, LibraryVersion } from "@kaba/shared";
import { api } from "../../lib/api";
import {
  dateText,
  refreshLibrary,
  scopeKey,
  sizeText,
  versionPreviewItem,
  type LibraryScope,
} from "../../lib/library";
import { toast } from "../../lib/toast";
import { AttachmentPreview } from "../card/AttachmentPreview";
import { Button, Sheet } from "../ui/ui";

/**
 * Fassungen einer Bibliotheksdatei (0.55): ansehen, herunterladen, eine ältere wieder zur aktuellen
 * machen (wird als neue Nummer angelegt – nichts geht verloren) oder eine ältere löschen.
 */
export function VersionsDialog({
  file,
  scope,
  canEdit,
  onNewVersion,
  onClose,
}: {
  file: LibraryFile | null;
  scope: LibraryScope;
  canEdit: boolean;
  onNewVersion: (f: LibraryFile) => void;
  onClose: () => void;
}) {
  const q = useQuery({
    queryKey: ["library", scopeKey(scope), "versions", file?.id, file?.version],
    queryFn: () =>
      api.get<LibraryVersion[]>(`/api/library/files/${file!.id}/versions`),
    enabled: !!file,
  });
  const [preview, setPreview] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const list = q.data ?? [];
  const run = async (fn: () => Promise<unknown>, ok: string) => {
    setBusy(true);
    try {
      await fn();
      toast.info(ok);
      await refreshLibrary(scope);
      await q.refetch();
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setBusy(false);
    }
  };
  const editable = canEdit && !file?.trashedAt;

  return (
    <Sheet
      open={!!file}
      onOpenChange={(o) => !o && onClose()}
      title={file ? `Fassungen von „${file.name}“` : "Fassungen"}
      side="center"
    >
      <div className="flex flex-col gap-3 p-4">
        {editable && file && (
          <div>
            <Button variant="primary" onClick={() => onNewVersion(file)}>
              <Upload size={16} aria-hidden /> Neue Fassung hochladen …
            </Button>
          </div>
        )}
        {q.isLoading ? (
          <p className="text-sm text-[var(--ink-soft)]">Lädt …</p>
        ) : (
          <ul
            className="flex flex-col divide-y divide-[var(--line)] rounded-xl border border-[var(--line)]"
            aria-label="Fassungen"
          >
            {list.map((v, i) => (
              <li
                key={v.id}
                className="flex flex-wrap items-center gap-2 px-3 py-2"
              >
                {v.thumbUrl ? (
                  <img
                    src={v.thumbUrl}
                    alt=""
                    className="size-11 shrink-0 rounded-lg object-cover"
                  />
                ) : null}
                <span className="min-w-0 flex-1 basis-48">
                  <span className="block text-sm font-medium">
                    Fassung {v.version}
                    {v.current && (
                      <span className="ml-2 rounded-md bg-accent-soft px-1.5 py-0.5 text-xs font-semibold">
                        aktuell
                      </span>
                    )}
                  </span>
                  <span className="block truncate text-xs text-[var(--ink-soft)]">
                    {v.name} · {sizeText(v.size)} · {dateText(v.createdAt)}
                    {v.uploaderName ? ` · ${v.uploaderName}` : ""}
                    {v.usage
                      ? ` · in ${v.usage === 1 ? "1 Karte" : `${v.usage} Karten`}`
                      : ""}
                  </span>
                </span>
                <span className="ml-auto flex shrink-0 flex-wrap justify-end gap-1">
                  <Button
                    variant="ghost"
                    aria-label={`Fassung ${v.version} ansehen`}
                    onClick={() => setPreview(i)}
                  >
                    <Eye size={16} aria-hidden />
                  </Button>
                  <a
                    href={v.downloadUrl}
                    download
                    aria-label={`Fassung ${v.version} herunterladen`}
                    className="inline-flex min-h-11 items-center justify-center rounded-lg px-3 hover:bg-black/5 sm:min-h-9 dark:hover:bg-white/10"
                  >
                    <Download size={16} aria-hidden />
                  </a>
                  {editable && !v.current && (
                    <>
                      <Button
                        variant="ghost"
                        disabled={busy}
                        onClick={() =>
                          void run(
                            () =>
                              api.post(`/api/library/versions/${v.id}/restore`),
                            `Fassung ${v.version} ist wieder aktuell (als Fassung ${(list[0]?.version ?? 0) + 1}).`,
                          )
                        }
                      >
                        <ArchiveRestore size={16} aria-hidden /> Wiederherstellen
                      </Button>
                      <Button
                        variant="ghost"
                        disabled={busy}
                        aria-label={`Fassung ${v.version} löschen`}
                        onClick={() => {
                          const note = v.usage
                            ? ` ${v.usage === 1 ? "Die Karte, die sie nutzt, behält" : `Die ${v.usage} Karten, die sie nutzen, behalten`} ihren Anhang.`
                            : "";
                          if (
                            window.confirm(
                              `Fassung ${v.version} endgültig löschen?${note}`,
                            )
                          )
                            void run(
                              () => api.del(`/api/library/versions/${v.id}`),
                              `Fassung ${v.version} gelöscht.`,
                            );
                        }}
                      >
                        <Trash2 size={16} aria-hidden />
                      </Button>
                    </>
                  )}
                </span>
              </li>
            ))}
          </ul>
        )}
        <p className="text-xs text-[var(--ink-soft)]">
          Karten behalten die Fassung, die sie angehängt haben. Unter
          „Verwendung“ bringst du sie auf die aktuelle Fassung.
        </p>
      </div>
      <AttachmentPreview
        items={list.map(versionPreviewItem)}
        index={preview}
        onIndex={setPreview}
        onClose={() => setPreview(null)}
      />
    </Sheet>
  );
}
